import { NavLink, Outlet } from 'react-router-dom'
import { LayoutGrid, List, ScanLine } from 'lucide-react'
import { cx } from '@/lib/cx'
import { ErrorBoundary } from './ErrorBoundary'
import { OfflineBanner } from './OfflineBanner'

const NAV = [
  { to: '/', label: 'Workflows', icon: LayoutGrid, end: true },
  { to: '/scan', label: 'Scan', icon: ScanLine, end: false },
  { to: '/history', label: 'History', icon: List, end: false },
] as const

/**
 * Persistent chrome around every screen.
 *
 * The shell renders with no data and no network — it is present on the first
 * paint. Screens fetch their own data underneath a route-scoped error
 * boundary, so a failure inside one screen leaves the navigation usable.
 */
export function AppShell() {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col">
      <OfflineBanner />

      {/* The notch is cleared here, once, for every route — including /admin,
          which draws its own header rather than using `Screen`. OfflineBanner
          sits above this and carries its own inset, because it sticks to the
          top of the viewport where this padding does not reach it. */}
      <main className="pt-safe flex-1 pb-28">
        <ErrorBoundary scope="route">
          <Outlet />
        </ErrorBoundary>
      </main>

      <nav
        aria-label="Main"
        className="pb-safe fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-md border-t border-[var(--border-subtle)] bg-[var(--surface-raised)]/95 backdrop-blur-xl"
      >
        <ul className="flex items-stretch justify-around px-2 py-1.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cx(
                    'flex min-h-touch flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 transition-colors',
                    isActive
                      ? 'text-brand-600'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="h-6 w-6" aria-hidden="true" />
                    {/* Labels stay visible. Icon-only navigation forces users to
                        learn glyphs and is a WCAG 3.3.2 problem. */}
                    <span className={cx('text-xs', isActive ? 'font-semibold' : 'font-medium')}>
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  )
}
